import http from "../utils/http";
import { notify } from "../utils/notify";
import { useGlobalStore } from "../store/useGlobalStore";
import { ForgetPasswordPayload, LoginPayload, ResetPasswordPayload } from "../@types/auth";
import { getCookieByValue, getErrorMessage } from "../utils/helpers";
import { basename } from "../utils/env";

export const handleLogin = async (payload: LoginPayload) => {
  try {
    console.log('Login attempt with:', payload.email);
    const res = await http.post('/authentication/login', payload);
    console.log('Login response received:', res.status);
    
    // Check if we received a response with data
    if (res.data) {
      console.log('Login successful, response data:', Object.keys(res.data));
      notify({ type: 'success', message: 'Connecté avec succès' });
      
      // Set user data in global store
      useGlobalStore.getState().setUser(res.data);
      
      // Check for authenticated cookie
      const isAuthenticated = getCookieByValue("authenticated");
      console.log('Authentication cookie present:', isAuthenticated ? 'Yes' : 'No');
      
      // Set a dummy Authorization header for compatibility
      http.defaults.headers.common['Authorization'] = 'Bearer session-auth';
      
      // Force cookie setting manually if needed
      if (!isAuthenticated) {
        console.log('Setting authenticated cookie manually');
        document.cookie = "authenticated=true; path=/; samesite=None; secure";
      }
      
      // Add a slight delay before redirect to ensure cookies are set
      setTimeout(() => {
        console.log('Redirecting to welcome page');
        // Force redirect with full URL to ensure it works in deployed environment
        window.location.href = `${window.location.origin}${basename}/app/welcome`;
      }, 500);
      
      return true;
    } else {
      console.error('Authentication failed - no response data');
      notify({ type: "error", message: "Authentication failed", description: "Please check your credentials" });
      return false;
    }
  } catch (err) {
    console.error('Login error:', err);
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}

export const handlelogout = async () => {
  try {
    await http.post('/authentication/logout');
    useGlobalStore.getState().logout()
    notify({ type: 'success', message: 'Logged out' })
    delete http.defaults.headers.common['Authorization'];
    
    // Clear the authentication cookies by setting them to expire
    document.cookie = 'authenticated=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    window.location.href = `${basename}/auth/login`
    return true;
  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}

export const getCurrentUser = async () => {
  try {
    // Check if we're authenticated via session cookie
    const isAuthenticated = getCookieByValue("authenticated");
    
    if (isAuthenticated === 'true') {
      // Set a dummy Authorization header for compatibility
      http.defaults.headers.common['Authorization'] = 'Bearer session-auth';
      
      // Get the current user data
      const res = await http.get('/users/current_user');
      if (res.data) {
        useGlobalStore.getState().setUser(res.data);
        return true;
      }
    }
    
    // If not authenticated or no user data, redirect to login
    window.location.href = `${basename}/auth/login`;
    return false;
  } catch (err) {
    console.error('Error getting current user:', err);
    // Clear any auth cookies on error
    document.cookie = 'authenticated=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Redirect to login
    window.location.href = `${basename}/auth/login`;
    return false;
  }
}

export const refreshAccessToken = async () => {
  try {
    // Check if we're authenticated via session cookie
    const isAuthenticated = getCookieByValue("authenticated");
    
    if (isAuthenticated === 'true') {
      // With session-based auth, we don't need to refresh tokens
      // Just verify that the session is still valid by making a simple request
      const response = await http.get('/users/current_user');
      
      if (response.data) {
        // Session is still valid, set the dummy auth header
        http.defaults.headers.common['Authorization'] = 'Bearer session-auth';
        return 'session-auth';
      }
    }
    
    // If we reach here, session is not valid or not present
    // Try to re-authenticate by redirecting to login
    window.location.href = `${basename}/auth/login`;
    return false;
  } catch (err) {
    // If we get an error, the session is probably invalid
    // Clear any auth cookies
    document.cookie = 'authenticated=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Redirect to login
    window.location.href = `${basename}/auth/login`;
    return false;
  }
}

export const handleForgetPassword = async (payload: ForgetPasswordPayload) => {
  try {
    delete http.defaults.headers.common['Authorization'];
    await http.post('/authentication/forget-password', payload);
    notify({ type: 'success', message: 'Reset email sent successfully', description: 'Check your inbox to reset your account\'s password' });
    return true;
  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}

export const handleResetPassword = async (token: string | undefined, payload: ResetPasswordPayload) => {
  try {
    delete http.defaults.headers.common['Authorization'];
    await http.post(`/authentication/password-reset/${token}`, payload);
    notify({ type: 'success', message: 'Password changed', description: 'You can login to your account' });
    return true;
  } catch (err) {
    notify({ type: "error", message: "Please try later", description: getErrorMessage(err) });
    return false;
  }
}
